import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ImpersonationService } from "../../common/lib/impersonationService";
import { ImpersonationSession } from "../../common/utils/impersonation.types";

interface ImpersonationContextType {
    impersonationSession: ImpersonationSession | null;
    isImpersonating: boolean;
    isAdmin: boolean | null;
    refreshImpersonationSession: () => void;
    checkAdminStatus: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
    const context = useContext(ImpersonationContext);
    if (context === undefined) {
        throw new Error("useImpersonation must be used within an ImpersonationProvider");
    }
    return context;
};

interface ImpersonationProviderProps {
    children: React.ReactNode;
}

export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({ children }) => {
    const [impersonationSession, setImpersonationSession] = useState<ImpersonationSession | null>(null);
    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    const queryClient = useQueryClient();
    const previousSessionRef = useRef<ImpersonationSession | null>(null);
    const isInitialMountRef = useRef<boolean>(true);

    const refreshImpersonationSession = () => {
        const session = ImpersonationService.getImpersonationSession();
        setImpersonationSession(session);
    };

    const checkAdminStatus = async () => {
        try {
            const adminStatus = await ImpersonationService.isAdmin();
            setIsAdmin(adminStatus);
        } catch (error) {
            console.error("Error checking admin status:", error);
            setIsAdmin(false);
        }
    };

    useEffect(() => {
        // Check admin status on mount
        checkAdminStatus();

        // Check for existing impersonation session
        refreshImpersonationSession();

        // Listen for storage changes (impersonation started/stopped in another tab)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "impersonation_session") {
                const newSession = e.newValue ? JSON.parse(e.newValue) : null;
                setImpersonationSession(newSession);
            }
        };

        window.addEventListener("storage", handleStorageChange);

        // Listen for custom events (impersonation started/stopped in same tab)
        const handleImpersonationChange = () => {
            refreshImpersonationSession();
        };

        window.addEventListener("impersonation-changed", handleImpersonationChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener("impersonation-changed", handleImpersonationChange);
        };
    }, []);

    // Clear entire React Query cache when impersonation starts or stops
    // This ensures that stale data from the previous user doesn't persist
    useEffect(() => {
        const previousSession = previousSessionRef.current;
        const currentSession = impersonationSession;

        // Skip on initial mount only - use a separate ref to track this
        if (isInitialMountRef.current) {
            // Initialize the refs for future comparisons
            previousSessionRef.current = currentSession;
            isInitialMountRef.current = false;
            return;
        }

        // Detect when impersonation state changes (start or stop)
        const previousIsImpersonating = previousSession?.isImpersonating === true;
        const currentIsImpersonating = currentSession?.isImpersonating === true;

        // If impersonation state changed (started or stopped), clear the entire cache
        if (previousIsImpersonating !== currentIsImpersonating) {
            queryClient.clear();
        }

        // Update the ref for the next comparison
        previousSessionRef.current = currentSession;
    }, [impersonationSession, queryClient]);

    const value: ImpersonationContextType = {
        impersonationSession,
        isImpersonating: impersonationSession?.isImpersonating === true,
        isAdmin,
        refreshImpersonationSession,
        checkAdminStatus,
    };

    return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
};
